import { emailSchema, isSsoReturnError, SSO_RETURN_ERRORS } from '@fluxus/shared';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { errorMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/session';
import { appSsoReturn, type AppSsoReturn } from './sso-apps';
import { newSsoVerifier, saveSsoPending, ssoBrowser } from './sso';

export type SsoTwoFactor = { token: string; step: 'verify' | 'setup'; email: string };

/**
 * "Entrar com SSO" (T-028d/T-028b, RF-04; DEC-073/074): pelo e-mail a API acha o login único da empresa e a pessoa
 * entra na página de login dela. Na web, a aba vai ao provedor e volta para /entrar/sso. No desktop, o navegador do
 * sistema abre e a volta chega pelo retorno local do app; no Android, o navegador in-app abre e a volta chega pelo
 * deep link. Nos apps, o verificador fica só na memória desta tela.
 */
export function SsoStartForm({
  initialEmail,
  onBack,
  onTwoFactor,
}: {
  initialEmail: string;
  onBack: (email: string) => void;
  /** A volta pediu a segunda etapa (2FA ativo ou exigido): a tela de login mostra o passo certo. */
  onTwoFactor: (challenge: SsoTwoFactor) => void;
}) {
  const { client } = useAuth();
  const [email, setEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState<string>();
  const [formError, setFormError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  /** Nos apps: esperando a pessoa concluir no navegador. */
  const [waiting, setWaiting] = useState(false);
  const pending = useRef<{ verifier: string; email: string } | null>(null);
  const appReturn = useRef<AppSsoReturn | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const ids = { email: useId(), alert: useId() };
  const isApp = client.platform !== 'web';

  // Para de esperar a volta ao sair da tela.
  useEffect(() => () => appReturn.current?.dispose(), []);

  function fail(err: unknown) {
    pending.current = null;
    appReturn.current?.dispose();
    appReturn.current = null;
    setWaiting(false);
    setSubmitting(false);
    setFormError(err);
  }

  async function finish(result: { code: string } | { error: string }) {
    const started = pending.current;
    appReturn.current?.dispose();
    appReturn.current = null;
    if ('error' in result || !started) {
      const code = 'error' in result ? result.error : 'LOGIN_EXPIRED';
      fail(new Error(isSsoReturnError(code) ? SSO_RETURN_ERRORS[code] : SSO_RETURN_ERRORS.LOGIN_EXPIRED));
      return;
    }
    pending.current = null;
    try {
      // Sessão aberta: a tela de login redireciona assim que o estado muda.
      const outcome = await client.completeSso(result.code, started.verifier);
      if (outcome.kind === 'two-factor') {
        onTwoFactor({
          token: outcome.challengeToken,
          step: outcome.step,
          email: outcome.email ?? started.email,
        });
      }
    } catch (err) {
      fail(err);
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setFormError(null);
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setEmailError(parsed.error.issues[0]?.message);
      emailRef.current?.focus();
      return;
    }
    setEmailError(undefined);
    setSubmitting(true);
    try {
      const { verifier, challenge } = await newSsoVerifier();
      if (!isApp) {
        if (!saveSsoPending({ verifier, email: parsed.data })) {
          throw new Error(
            'O navegador está bloqueando o armazenamento desta página, e o login único precisa dele. Libere o armazenamento ou entre com e-mail e senha.',
          );
        }
        // O navegador sai do app: o botão segue em "carregando" até a página do provedor abrir.
        ssoBrowser.open(await client.startSso(parsed.data, challenge));
        return;
      }
      pending.current = { verifier, email: parsed.data };
      const ret = await appSsoReturn(client.platform, (result) => void finish(result));
      appReturn.current = ret;
      const url = await client.startSso(parsed.data, challenge, ret.returnTo);
      await ret.open(url);
      setWaiting(true);
    } catch (err) {
      fail(err);
      emailRef.current?.focus();
    }
  }

  function cancel() {
    appReturn.current?.dispose();
    appReturn.current = null;
    pending.current = null;
    setWaiting(false);
    setSubmitting(false);
  }

  return (
    <>
      <h1 className="text-3xl font-extrabold tracking-tight">Entrar com SSO</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Use o e-mail da sua empresa: você entra na página de login dela e volta para o Fluxus BSC.
      </p>
      {waiting ? (
        <div className="mt-8 flex flex-col gap-5">
          <div role="status" aria-live="polite">
            <FormAlert tone="locked" title="Continue no navegador">
              Conclua a entrada na página da sua empresa que abriu no navegador. Depois, o Fluxus BSC continua
              sozinho.
            </FormAlert>
          </div>
          <Button type="button" variant="outline" size="lg" className="w-full" onClick={cancel}>
            Cancelar
          </Button>
        </div>
      ) : (
        <form
          noValidate
          onSubmit={onSubmit}
          className="mt-8 flex flex-col gap-5"
          aria-describedby={formError ? ids.alert : undefined}
        >
          <FormField id={ids.email} label="E-mail da empresa" error={emailError}>
            <Input
              ref={emailRef}
              autoFocus
              {...fieldA11y(ids.email, emailError)}
              type="email"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError(undefined);
              }}
              className="h-11"
            />
          </FormField>
          {formError ? (
            <FormAlert id={ids.alert} tone="error">
              {errorMessage(formError)}
            </FormAlert>
          ) : null}
          <Button type="submit" size="lg" className="mt-1 w-full" loading={submitting}>
            Continuar
          </Button>
          <Button type="button" variant="link" disabled={submitting} onClick={() => onBack(email)}>
            Entrar com e-mail e senha
          </Button>
        </form>
      )}
    </>
  );
}
